import { atom } from "jotai";

import type { AuthUser } from "@/types/auth";

const IS_ELECTRON =
  typeof window !== "undefined" && Boolean(window.electronAPI);

/**
 * Browser PWA: accessKey is unused — auth is via the HttpOnly `eriol_session`
 * cookie that the backend sets on login. JS can't read it (that's the point).
 *
 * Electron desktop: the deep-link flow still hands an accessKey across
 * processes (custom protocol → IPC), and we still persist it in localStorage
 * + send it as the Authorization header. There's no cookie path to bridge
 * external browser → Electron's WebView session yet.
 *
 * The accessKey/userId atoms therefore only carry meaning in Electron. In
 * browser they stay null and the user atom is the source of truth.
 */
export const accessKeyAtom = atom<string | null>(
  IS_ELECTRON ? localStorage.getItem("@Guest/accessKey") : null,
);

export const userIdAtom = atom<string | null>(
  IS_ELECTRON ? localStorage.getItem("@Guest/userId") : null,
);

export const userAtom = atom<AuthUser | null>(null);

/**
 * Tri-state validation:
 *   - "pending": at mount, before backend validation
 *   - "valid": backend confirmed active rom (or no token at all → treated as "not logged in")
 *   - "invalid": backend said the token/rom is not active → force logout
 */
export type AuthValidationState = "pending" | "valid" | "invalid";

export const authValidationStateAtom = atom<AuthValidationState>(
  // In browser the cookie may exist invisibly to us, so always start in pending
  // until /me resolves. In Electron we know up front whether we have a token.
  IS_ELECTRON
    ? localStorage.getItem("@Guest/accessKey")
      ? "pending"
      : "valid"
    : "pending",
);

/**
 * Single source of truth for "is the user logged in?": did /auth/me return
 * a user. Covers both the browser (cookie path) and Electron (header path)
 * since both lead to a populated userAtom on success.
 */
export const isAuthenticatedAtom = atom((get) => {
  const hasUser = get(userAtom) !== null;
  if (!hasUser) return false;
  return get(authValidationStateAtom) !== "invalid";
});
